import {
    EditorGeneration,
    ManifestWorkflowEditor,
    Profile,
    WorkflowEditorV3SupportedEntry,
    WorkflowService,
} from "services/workflows/WorkflowService";
import { logger } from "utils/logging";

const MINIMUM_VERSIONS_FOR_RESTORE_PREVIEW: Record<string, string> = {
    bde: "7.18.0",
    bmde: "5.10.0",
    bmde_diagnostic: "5.10.0",
    bmp: "3.4.0",
    lun: "2.12.0",
};

export const trackFetchLoaded = <T>(
    fetchPromise: Promise<T>,
    abortController: AbortController,
    onSuccess: (data: T) => void,
    setLoaded: (loaded: boolean) => void,
    failureLogMessage: string
): void => {
    fetchPromise
        .then((data) => {
            onSuccess(data);
            setLoaded(true);
        })
        .catch(() => {
            if (abortController.signal.aborted) {
                return;
            }
            logger.warn(failureLogMessage);
            setLoaded(true);
        });
};

export const compareEditorVersionDtos = (left: ManifestWorkflowEditor, right: ManifestWorkflowEditor): number => {
    const baseVersionCompare = compareEditorVersions(left.version, right.version);
    return baseVersionCompare !== 0
        ? baseVersionCompare
        : new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime();
};

const getBaseVersion = (version: string): string => version.replace(/[a-z]+\d*$/i, "");

const getSuffixOrder = (suffix: string): number => {
    const lower = suffix.toLowerCase();
    if (!lower) {
        return 3;
    }
    if (lower.startsWith("rc")) {
        return 2 + (parseInt(lower.substring(2)) || 1) * 0.01;
    }
    if (lower.startsWith("a")) {
        return 1 + (parseInt(lower.substring(1)) || 1) * 0.01;
    }
    return 0;
};

const getVersionSuffix = (version: string, base: string): string => version.replace(base, "");

export const compareEditorVersions = (left: string, right: string): number => {
    const leftBase = getBaseVersion(left);
    const rightBase = getBaseVersion(right);

    if (leftBase !== rightBase) {
        const leftParts = leftBase.split(".").map(Number);
        const rightParts = rightBase.split(".").map(Number);

        for (let i = 0; i < Math.max(leftParts.length, rightParts.length); i++) {
            const delta = (rightParts[i] || 0) - (leftParts[i] || 0);
            if (delta !== 0) {
                return delta;
            }
        }
    }

    return getSuffixOrder(getVersionSuffix(right, rightBase)) - getSuffixOrder(getVersionSuffix(left, leftBase));
};

export const deduceEditorVersion = (editorVersion: string): string => {
    const lower = editorVersion.toLowerCase();
    if (lower.includes("dev")) {
        return lower.replace("dev", "");
    }
    if (lower.includes("rc")) {
        return lower.split("rc")[0];
    }
    if (lower.includes("a")) {
        return lower.split("a")[0];
    }
    return lower;
};

export const isNewerEditorAvailable = (
    profile: Profile,
    editorGeneration: EditorGeneration,
    editorVersion: string,
    latestVersions?: LatestEditorVersions,
    v3Supported?: WorkflowEditorV3SupportedEntry[]
) => {
    if (editorGeneration === "V3") {
        return (
            v3Supported?.some(
                (entry) => entry.profile === profile && compareEditorVersions(editorVersion, entry.version) > 0
            ) ?? false
        );
    }

    if (editorGeneration !== "V2") {
        // Backend may not yet report editorGeneration="V3" for all V3-supported
        // workflows/templates. Fall back to a V3 manifest check before assuming
        // the workflow is outdated.
        if (v3Supported?.some((entry) => entry.profile === profile && entry.version === editorVersion)) {
            return v3Supported.some(
                (entry) => entry.profile === profile && compareEditorVersions(editorVersion, entry.version) > 0
            );
        }
        return true;
    }

    const hasNewerV3 =
        v3Supported?.some(
            (entry) => entry.profile === profile && compareEditorVersions(editorVersion, entry.version) > 0
        ) ?? false;
    if (hasNewerV3) {
        return true;
    }

    switch (profile) {
        case Profile.BDE:
            return compareEditorVersions(editorVersion, latestVersions?.bde ?? "") > 0;
        case Profile.BMDE:
            return compareEditorVersions(editorVersion, latestVersions?.bmde ?? "") > 0;
        case Profile.LUN:
            return compareEditorVersions(editorVersion, latestVersions?.lun ?? "") > 0;
        case Profile.BMDE_DIAGNOSTIC:
            return compareEditorVersions(editorVersion, latestVersions?.bmdeDiag ?? "") > 0;
        case Profile.BMP:
            return compareEditorVersions(editorVersion, latestVersions?.bmp ?? "") > 0;
        default:
            return true;
    }
};

export interface LatestEditorVersions {
    bde: string;
    bmde: string;
    bmdeDiag: string;
    lun: string;
    bmp: string;
}

export const deduceLatestEditorVersions = (workflowEditors: ManifestWorkflowEditor[]): LatestEditorVersions => {
    const v2Editors = workflowEditors.filter((each) => each.editorGeneration === "V2").sort(compareEditorVersionDtos);

    const deduplicatedEditors = v2Editors.filter(
        (obj, index, self) => index === self.findIndex((each) => each.version === obj.version)
    );

    const getLatestForProfile = (profiles: Profile[]) => {
        return deduplicatedEditors.find((editor) => profiles.includes(editor.profile as Profile))?.version || "";
    };

    return {
        bde: getLatestForProfile([Profile.BDE]),
        lun: getLatestForProfile([Profile.LUN]),
        bmp: getLatestForProfile([Profile.BMP]),
        bmde: getLatestForProfile([Profile.BMDE, Profile.BMDE_DIAGNOSTIC]),
        bmdeDiag: getLatestForProfile([Profile.BMDE, Profile.BMDE_DIAGNOSTIC]),
    };
};

export const normalizeWorkflowProfile = (profile: string | undefined): Profile => {
    switch (profile) {
        case Profile.BMDE:
        case Profile.BMDE_DIAGNOSTIC:
            return Profile.BMDE;
        case Profile.LUN:
            return Profile.LUN;
        case Profile.BMP:
            return Profile.BMP;
        default:
            return Profile.BDE;
    }
};

export const isEditorV3Supported = (
    supported: WorkflowEditorV3SupportedEntry[],
    profile: string,
    version: string
): boolean => supported.some((entry) => entry.profile === profile && entry.version === version);

export const isProfileV3Supported = (supported: WorkflowEditorV3SupportedEntry[], profile: string): boolean =>
    supported.some((entry) => entry.profile === profile);

export const mergeAndSortEditorVersions = (
    v2Versions: string[],
    v3Supported: WorkflowEditorV3SupportedEntry[],
    profile: string
): string[] => {
    const v3OnlyVersions = v3Supported
        .filter((entry) => entry.profile === profile && !v2Versions.includes(entry.version))
        .map((entry) => entry.version);
    return [...v2Versions, ...v3OnlyVersions].sort(compareEditorVersions);
};

export const supportsEditorBasedRestore = (profile: string, editorVersion: string): boolean => {
    const profileLower = profile.toLowerCase();

    const minVersion = MINIMUM_VERSIONS_FOR_RESTORE_PREVIEW[profileLower];
    if (!minVersion) {
        return false;
    }

    const cleanEditorVersion = deduceEditorVersion(editorVersion);
    return compareEditorVersions(cleanEditorVersion, minVersion) <= 0;
};

export interface EditorDecision {
    openV3: boolean;
    profile: string;
    version: string;
    editorGeneration?: EditorGeneration;
}

export const determineEditorToOpen = (
    profile: string,
    version: string,
    workflowEditorV3Supported: WorkflowEditorV3SupportedEntry[],
    workflowEditors: ManifestWorkflowEditor[],
    workflowEditorV3Flag: boolean,
    determineEditorGenerationByProfileAndVersion: (
        profile: Profile,
        version: string,
        fallbackToV2: boolean
    ) => EditorGeneration
): EditorDecision => {
    const lowercaseProfile = profile.toLowerCase();

    // bmde and bmde_diagnostic share a single V2 editor, published under the "bmde" profile only.
    const normalizedProfile = normalizeWorkflowProfile(lowercaseProfile);

    const openV2WithDeducedGeneration = (): EditorDecision => ({
        openV3: false,
        profile: lowercaseProfile,
        version,
        editorGeneration: determineEditorGenerationByProfileAndVersion(normalizedProfile, version, true),
    });

    if (!workflowEditorV3Flag) {
        return openV2WithDeducedGeneration();
    }

    if (isEditorV3Supported(workflowEditorV3Supported, lowercaseProfile, version)) {
        return { openV3: true, profile: lowercaseProfile, version };
    }

    const version2Editor = workflowEditors.find((editor) => {
        const editorNormalizedProfile = normalizeWorkflowProfile(editor.profile.toLowerCase());
        return editorNormalizedProfile === normalizedProfile && editor.version === version;
    });

    if (version2Editor) {
        return {
            openV3: false,
            profile: lowercaseProfile,
            version,
            editorGeneration: version2Editor.editorGeneration,
        };
    }

    // Workflow file has unsupported/unknown version. Open with latest available editor for the product.
    if (isProfileV3Supported(workflowEditorV3Supported, lowercaseProfile)) {
        return { openV3: true, profile: lowercaseProfile, version };
    }

    return openV2WithDeducedGeneration();
};

export const fetchWorkflowEditorVersion3SupportedIfEnabled = (
    enabled: boolean,
    workflowService: WorkflowService,
    abortControllers: AbortController[],
    onSuccess: (entries: WorkflowEditorV3SupportedEntry[]) => void
): Promise<void> => {
    if (!enabled) {
        return Promise.resolve();
    }

    const abortController = new AbortController();
    abortControllers.push(abortController);
    return workflowService
        .fetchWorkflowEditorV3Supported(abortController)
        .then(onSuccess)
        .catch(() => {
            logger.error("Failed to fetch V3 supported versions; V3 editor options will not be shown.");
        });
};
